import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import { useParams } from "react-router-dom";
import {
  formatCurrency,
  mergeRunEstimationsByDate,
} from "@via/pages/organization/helpers.tsx";
import {
  useGetOrganizationRunEstimations,
  useGetOrganizationWorkbenchEstimations,
} from "@via/pages/organization/organization.api.tsx";
import CostEstimationBanner from "@via/pages/organization/usage/cost-estimation-banner.tsx";
import NoUsageDisclaimer from "@via/pages/organization/usage/no-usage-disclaimer.tsx";
import UsageBarCharts from "@via/pages/organization/usage/usage-bar-charts.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import {
  GetOrganizationWorkbenchEstimationsResponseBody,
  RunEstimation,
} from "common";
import { useCallback } from "react";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext.tsx";
import { ROUTES } from "@via/utils/constants";

const usageCurrency = "usd";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);
const columnHelper = createColumnHelper<RunEstimation>();
const workbenchColumnHelper =
  createColumnHelper<GetOrganizationWorkbenchEstimationsResponseBody[0]>();

const Usage = () => {
  const { organizationId = "" } = useParams();

  const columns = [
    columnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("totalCost", {
      cell: info => (
        <div>{formatCurrency(info.getValue(), usageCurrency, 2)}</div>
      ),
      header: () => <span>Total Cost</span>,
    }),
    columnHelper.accessor("totalCpuTime", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Total Cpu Time (CPU⋅min)</span>,
    }),
    columnHelper.accessor("totalMemoryTime", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Total Memory Time (GB⋅min)</span>,
    }),
    columnHelper.accessor("totalDiskTime", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Total Disk Time (GB⋅min)</span>,
    }),
    columnHelper.accessor("username", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Owner</span>,
    }),
    columnHelper.accessor("runEndDate", {
      cell: info => {
        return formatDateString(info.getValue(), "LL");
      },
      header: () => <span>Date</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<RunEstimation>>;

  const workbenchColumns = [
    workbenchColumnHelper.accessor("workbench", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Workbench</span>,
      enableSorting: false,
    }),
    workbenchColumnHelper.accessor("name", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Name</span>,
      enableSorting: false,
    }),
    workbenchColumnHelper.accessor("workbenchInstanceId", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Workbench Instance Id</span>,
      enableSorting: false,
    }),
    workbenchColumnHelper.accessor("monthDate", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Month Date</span>,
      enableSorting: false,
    }),
    workbenchColumnHelper.accessor("cost", {
      cell: info => <div>{info.getValue()}</div>,
      header: () => <span>Cost</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<GetOrganizationWorkbenchEstimationsResponseBody[0]>>;

  const {
    data: organizationRunEstimations = [],
    isLoading: isOrganizationRunEstimationsLoading,
  } = useGetOrganizationRunEstimations({ id: organizationId });

  const {
    data: organizationWorkbenchEstimations = [],
    isLoading: isOrganizationWorkbenchEstimationsLoading,
  } = useGetOrganizationWorkbenchEstimations(organizationId);
  const formattedRunEstimations = mergeRunEstimationsByDate(
    organizationRunEstimations
  );

  const getUsageContext = useCallback(() => {
    // Calculate summary statistics
    const totalRunCost = organizationRunEstimations.reduce(
      (sum, run) => sum + (run.totalCost || 0),
      0
    );
    const totalCpuTime = organizationRunEstimations.reduce(
      (sum, run) => sum + (run.totalCpuTime || 0),
      0
    );
    const totalMemoryTime = organizationRunEstimations.reduce(
      (sum, run) => sum + (run.totalMemoryTime || 0),
      0
    );
    const totalDiskTime = organizationRunEstimations.reduce(
      (sum, run) => sum + (run.totalDiskTime || 0),
      0
    );
    const totalWorkbenchCost = organizationWorkbenchEstimations.reduce(
      (sum, workbench) => sum + (Number(workbench.cost) || 0),
      0
    );

    return {
      organizationId,
      summary: {
        totalRunCost,
        totalCpuTime,
        totalMemoryTime,
        totalDiskTime,
        totalWorkbenchCost,
        totalCost: totalRunCost + totalWorkbenchCost,
        runCount: organizationRunEstimations.length,
        workbenchCount: organizationWorkbenchEstimations.length,
      },
      runEstimations: organizationRunEstimations.slice(0, 50).map(run => ({
        name: run.name,
        totalCost: run.totalCost,
        totalCpuTime: run.totalCpuTime,
        totalMemoryTime: run.totalMemoryTime,
        totalDiskTime: run.totalDiskTime,
        username: run.username,
        runEndDate: run.runEndDate,
      })),
      workbenchEstimations: organizationWorkbenchEstimations
        .slice(0, 50)
        .map(workbench => ({
          workbench: workbench.workbench,
          name: workbench.name,
          workbenchInstanceId: workbench.workbenchInstanceId,
          monthDate: workbench.monthDate,
          cost: workbench.cost,
        })),
      hasMoreRuns: organizationRunEstimations.length > 50,
      hasMoreWorkbenches: organizationWorkbenchEstimations.length > 50,
    };
  }, [
    organizationId,
    organizationRunEstimations,
    organizationWorkbenchEstimations,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.ORGANIZATION_USAGE,
    contextName: "Organization Usage",
    getContext: getUsageContext,
    systemPrompt:
      "You are analyzing organization usage and cost estimation page. Help answer questions about run costs, CPU/memory/disk usage, workbench costs, and usage statistics.",
    enabled: !!organizationId,
  });

  return (
    <div className="my-5 w-full">
      {isOrganizationRunEstimationsLoading ||
        (isOrganizationWorkbenchEstimationsLoading && (
          <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonLoader count={12} />
          </div>
        ))}
      {organizationRunEstimations?.length ? (
        <>
          <CostEstimationBanner />
          <UsageBarCharts runEstimations={formattedRunEstimations} />
          <div className="my-4">
            <h2 className="via-h2">Organization Runs</h2>
            <Table
              columns={columns}
              data={organizationRunEstimations}
              total={organizationRunEstimations?.length}
              size={TableSize.Big}
            />
          </div>
        </>
      ) : null}
      {organizationWorkbenchEstimations?.length ? (
        <div className="my-4">
          <h2 className="via-h2">Workbench Cost Estimations</h2>
          <Table
            columns={workbenchColumns}
            data={organizationWorkbenchEstimations}
            total={organizationWorkbenchEstimations?.length}
            size={TableSize.Big}
          />
        </div>
      ) : null}

      {!organizationRunEstimations?.length &&
      !organizationWorkbenchEstimations?.length ? (
        <NoUsageDisclaimer />
      ) : null}
    </div>
  );
};

export default Usage;
